import React, { useState, useRef } from 'react';
import { X, Image as ImageIcon, Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { supabase } from '../lib/supabaseClient';
import { plainTextToHtml } from '../lib/utils';

export default function CertificatePreviewModal({ model, overrides, onClose, readonly = true }) {
  const [isDownloading, setIsDownloading] = useState(false);

  const htmlPreviewRef = useRef(null);
  const previewAreaRef = useRef(null);
  const frontPrintRef = useRef(null);
  const backPrintRef = useRef(null);

  const buildPreviewHtml = (html, varsObj = {}) => {
    if (!html) return '';
    const defaults = {
      aluno: 'João da Silva',
      curso: 'Suporte Básico de Vida',
      carga_horaria: '40:00',
      nota_final: '9,5',
      data_conclusao: '10 de janeiro de 2026',
      validade: '24 meses',
      codigo_verificacao: 'ABCD-1234-EFGH',
      instrutor: 'Dr. João Pereira',
      coordenador: 'Ana Souza',
      parceiro_sas: 'Parceiro SAS',
      conteudo_programatico: 'Módulo I: Introdução\nMódulo II: Prática\nMódulo III: Avaliação',
      empresa: 'Empresa Exemplo Ltda',
      hash_code: 'INLWMHME9DBH',
      qrcode_url: (typeof window !== 'undefined' && window.location?.origin)
        ? `${window.location.origin}/validar?code=ABCD-1234-EFGH`
        : '/validar?code=ABCD-1234-EFGH',
    };
    const vars = { ...defaults, ...varsObj };
    const applyCase = (val, tokenRaw) => {
      const isUpper = tokenRaw === tokenRaw.toUpperCase();
      const isLower = tokenRaw === tokenRaw.toLowerCase();
      if (isUpper) return String(val).toUpperCase();
      if (isLower) return String(val).toLowerCase();
      return String(val);
    };
    const replaced = html.replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, (m, keyRaw) => {
      const key = keyRaw.toLowerCase();
      const val = vars[key] != null ? vars[key] : '';
      const htmlVal = String(val).replace(/\n/g, '<br/>');
      return applyCase(htmlVal, keyRaw);
    });
    const sanitize = (srcHtml) => {
      return String(srcHtml || '').replace(/<img[^>]+src=["']([^"']+)["'][^>]*>/gi, (m, src) => {
        try {
          const base = (typeof window !== 'undefined' && window.location?.origin) || 'http://localhost';
          const u = new URL(src, base);
          const host = (u.hostname || '').toLowerCase();
          const ok = src.startsWith('data:') || src.startsWith('/') || /supabase\.co$/i.test(host) || /api\.qrserver\.com$/i.test(host) || /localhost|127\.0\.0\.1/i.test(host);
          if (ok) return m;
          return '<div style="font-size:10px;color:#64748b;text-align:center;">Imagem externa bloqueada no preview</div>';
        } catch {
          return '<div style="font-size:10px;color:#64748b;text-align:center;">Imagem externa bloqueada no preview</div>';
        }
      });
    };
    return sanitize(replaced);
  };

  const buildBackStackHtml = (m) => {
    const header = m?.backHeaderHtml || '';
    const main = m?.backMainHtml || '';
    const institutional = m?.backInstitutionalHtml || '';
    return `<div data-back-stack="true">` +
      `<div data-back-header="true" style="margin:0 0 10px 0;">${header}</div>` +
      `<div data-back-main="true" style="margin:0 0 14px 0;">${main}</div>` +
      `<div data-back-institutional="true" style="margin:0;">${institutional}</div>` +
    `</div>`;
  };

  const extractLegacyDeclarationHtml = (html) => {
    const src = String(html || '');
    const m = src.match(/<div[^>]*data-averbacao=\"true\"[^>]*>([\s\S]*?)<\/div>/);
    return m ? String(m[1] || '').trim() : '';
  };

  const stripLegacyDeclarationContainer = (html) => {
    const src = String(html || '');
    return src.replace(/<div[^>]*data-averbacao=\"true\"[^>]*>[\s\S]*?<\/div>/, '').trim();
  };

  const inferLegacyQrEnabled = (html) => /data-qr=\"true\"|qrserver|qrcode_url/i.test(String(html || ''));

  const getDefaultBackSections = () => {
    const header = `<p><strong>{{CURSO}}</strong></p>`;
    const main = `<p>Aluno: <strong>{{aluno}}</strong> &nbsp;&nbsp; Carga Horária: <strong>{{carga_horaria}}</strong></p><p>Nota Final: <strong>{{nota_final}}</strong></p><p><strong>Conteúdo programático</strong></p><p>{{conteudo_programatico}}</p>`;
    const institutional = `<p>Instrutor: <strong>{{instrutor}}</strong></p><p>Coordenador: <strong>{{coordenador}}</strong></p>`;
    const declaration = `<p>Este curso foi disponibilizado ao(à) colaborador(a) <strong><em>{{aluno}}</em></strong> pela empresa <strong><em>{{empresa}}</em></strong>, no âmbito de sua política de capacitação e desenvolvimento profissional.</p>`;
    return { header, main, institutional, declaration };
  };

  const normalizeBackFromLegacy = (m) => {
    const legacyHtml = m?.customTextBack || '';
    const declarationFromMeta = m?.averbacaoText ? plainTextToHtml(m.averbacaoText) : '';
    const declarationFromHtml = extractLegacyDeclarationHtml(legacyHtml);
    const base = stripLegacyDeclarationContainer(legacyHtml);
    const defaults = getDefaultBackSections();
    return {
      header: m?.backHeaderHtml || defaults.header,
      main: m?.backMainHtml || (base || defaults.main),
      institutional: m?.backInstitutionalHtml || defaults.institutional,
      declaration: m?.backDeclarationHtml || declarationFromMeta || declarationFromHtml || defaults.declaration,
      showQr: typeof m?.backShowQrCode === 'boolean' ? m.backShowQrCode : inferLegacyQrEnabled(legacyHtml),
    };
  };

  const handleDownloadPDF = async () => {
    // Se tivermos um ID de certificado persistido, usamos a geração via backend (PDFShift)
    if (overrides?.certId) {
      setIsDownloading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pdfshift`;
        
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ certificate_id: overrides.certId }),
        });

        if (!response.ok) {
           const errData = await response.json();
           throw new Error(errData.error || 'Erro ao gerar PDF no servidor');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Certificado-${(overrides?.curso || '').replace(/[^a-z0-9]/gi, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

      } catch (err) {
        console.error('Erro ao gerar PDF via Server', err);
        alert('Erro ao gerar PDF no servidor: ' + err.message + '\n\nTentando gerar localmente...');
        // Fallback para local
        generateLocalPDF();
      } finally {
        setIsDownloading(false);
      }
      return;
    }

    generateLocalPDF();
  };

  const generateLocalPDF = async () => {
    const frontEl = frontPrintRef.current;
    const backEl = backPrintRef.current;
    if (!frontEl || !backEl) return;

    setIsDownloading(true);
    try {
      // Temporarily make visible for capture (trick html2canvas)
      const parent = frontEl.parentElement;
      const originalStyle = {
        position: parent.style.position,
        left: parent.style.left,
        top: parent.style.top,
        visibility: parent.style.visibility,
        zIndex: parent.style.zIndex
      };
      
      // Move into viewport but behind everything
      parent.style.position = 'fixed';
      parent.style.left = '0';
      parent.style.top = '0';
      parent.style.visibility = 'visible';
      parent.style.zIndex = '-9999';

      // Small delay to ensure render
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const commonOptions = {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        foreignObjectRendering: false, 
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
        width: 1280,
        height: 720,
        windowWidth: 1280,
        windowHeight: 720,
        onclone: (doc) => {
            const images = doc.getElementsByTagName('img');
            for (let img of images) {
                img.style.display = 'block';
                img.style.visibility = 'visible';
            }
            // Ensure fonts and styles are properly inherited in the clone
            const printContainers = doc.querySelectorAll('[data-cert-root="true"]');
            printContainers.forEach(container => {
                container.style.color = 'inherit';
                const allChildren = container.querySelectorAll('*');
                allChildren.forEach(child => {
                   if (child.style.color === '' && !child.hasAttribute('color')) {
                       child.style.color = 'inherit';
                   }
                });
            });
        }
      };

      // Se for useHtml e o frontEl tiver múltiplos filhos (páginas), vamos capturá-los separadamente
       if (model.useHtml) {
           const root = frontEl.querySelector('[data-cert-root="true"]');
           // Preferir capturar as páginas explícitas do HTML único
           let children = root ? Array.from(root.querySelectorAll('.certificate')) : [];
           if (children.length === 0 && root) {
             children = Array.from(root.children);
             if (children.length === 1 && children[0].children.length > 1) {
               children = Array.from(children[0].children);
             }
           }
           
           if (children.length === 1) {
              const innerPages = children[0].querySelectorAll('.page, [data-page], section, div > div:nth-child(n)');
              if (innerPages.length > 1) {
                children = Array.from(innerPages).slice(0, 2);
              }
           }
           
           if (children.length > 1) {
                const pdfHtml = new jsPDF({ orientation: 'landscape', unit: 'px', format: [1200, 848] });
               for (let i = 0; i < children.length; i++) {
                      if (i > 0) pdfHtml.addPage([1200, 848], 'landscape');
                     const canvas = await html2canvas(children[i], { 
                         ...commonOptions, 
                         width: 1200, 
                          height: 848, 
                         windowWidth: 1200, 
                          windowHeight: 848,
                         onclone: (doc) => {
                           const body = doc.body;
                            body.style.width = '1200px';
                            body.style.height = '848px';
                           body.style.overflow = 'hidden';
                         }
                     });
                      pdfHtml.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, 1200, 848);
                 }
               
               // Restore original style
               parent.style.position = originalStyle.position;
               parent.style.left = originalStyle.left;
               parent.style.top = originalStyle.top;
               parent.style.visibility = originalStyle.visibility;
               parent.style.zIndex = originalStyle.zIndex;
               
               const fileNameHtml = readonly 
                 ? `Certificado-${(overrides?.curso || '').replace(/[^a-z0-9]/gi, '_')}.pdf`
                 : `${model.name}.pdf`;
               pdfHtml.save(fileNameHtml);
               return;
           }
       }

       // Capture Front (Fallback for single long page)
       const canvasFront = await html2canvas(frontEl, commonOptions);
       if (canvasFront.width === 0 || canvasFront.height === 0) throw new Error('Canvas Preview renderizado com dimensões 0x0.');
       const imgFront = canvasFront.toDataURL('image/png');
       
       const pdf = new jsPDF({
         orientation: 'landscape',
         unit: 'px',
         format: [1200, 848] // Using pixel dimensions directly
       });
       
       const ratio = canvasFront.height / canvasFront.width;
       
        if (ratio > 0.8) {
          // Se for uma imagem muito alta (HTML colado sem divs filhas limpas)
           pdf.addImage(imgFront, 'PNG', 0, 0, 1200, 1200 * ratio);
           pdf.addPage([1200, 848], 'landscape');
           pdf.addImage(imgFront, 'PNG', 0, -848, 1200, 1200 * ratio);
       } else {
          pdf.addImage(imgFront, 'PNG', 0, 0, 1200, 848);
          
          if (backEl) {
              // Legacy back capture
              const canvasBack = await html2canvas(backEl, commonOptions);
              if (canvasBack.width > 0 && canvasBack.height > 0) {
                  pdf.addPage([1200, 848], 'landscape');
                  pdf.addImage(canvasBack.toDataURL('image/png'), 'PNG', 0, 0, 1200, 848);
              }
          }
       }

      // Restore original style
      parent.style.position = originalStyle.position;
      parent.style.left = originalStyle.left;
      parent.style.top = originalStyle.top;
      parent.style.visibility = originalStyle.visibility;
      parent.style.zIndex = originalStyle.zIndex;

      const fileName = readonly 
        ? `Certificado-${(overrides?.curso || '').replace(/[^a-z0-9]/gi, '_')}.pdf`
        : `${model.name}.pdf`;
      pdf.save(fileName);
      
    } catch (err) {
      console.error('Erro ao gerar PDF', err);
      alert('Erro ao gerar PDF: ' + err.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const layout = {
    front: { x: typeof model.xFront === 'number' ? model.xFront : 15, y: typeof model.yFront === 'number' ? model.yFront : 18, width: typeof model.widthFront === 'number' ? model.widthFront : 70, lineHeight: typeof model.lineHeightFront === 'number' ? model.lineHeightFront : 1.6 },
    back: {
      x: typeof model.xBack === 'number' ? model.xBack : 15,
      y: typeof model.yBack === 'number' ? model.yBack : 18,
      width: typeof model.widthBack === 'number' ? model.widthBack : 70,
      lineHeight: typeof model.lineHeightBack === 'number' ? model.lineHeightBack : 1.6,
      qr: {
        x: typeof model.backQrX === 'number' ? model.backQrX : 85,
        y: typeof model.backQrY === 'number' ? model.backQrY : 50,
      },
      declaration: {
        x: typeof model.backDeclarationX === 'number' ? model.backDeclarationX : 50,
        y: typeof model.backDeclarationY === 'number' ? model.backDeclarationY : 85,
        width: typeof model.backDeclarationWidth === 'number' ? model.backDeclarationWidth : (typeof model.widthBack === 'number' ? model.widthBack : 70),
      },
    },
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4 animate-fade-in" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-[90vw] shadow-2xl p-6 md:p-10 max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 border-b border-slate-800 pb-3 flex justify-between items-start pt-1">
          <div>
            <h2 className="text-2xl font-bold text-white leading-tight break-words pr-4">{readonly ? overrides?.curso || 'Certificado' : model.name}</h2>
            <p className="text-slate-400 text-sm mt-1">Visualização do Certificado</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition shrink-0 p-1 hover:bg-slate-800 rounded-lg"><X size={24} /></button>
        </div>

        <div className="mb-5 flex items-center justify-between gap-4 border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-widest border-b-2 border-teal-500 text-white">
            <ImageIcon size={16} /> Preview
          </div>
          <button onClick={handleDownloadPDF} disabled={isDownloading} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-bold bg-teal-600 hover:bg-teal-500 transition disabled:opacity-50 shadow-lg shadow-teal-900/20" title="Baixar PDF da visualização atual">
            <Download size={14} /> {isDownloading ? 'Gerando PDF...' : 'Baixar PDF'}
          </button>
        </div>

        <div className="flex-1 overflow-auto flex justify-center bg-slate-950/50 rounded-lg p-2 md:p-4">
          {model.useHtml ? (
            <div className="relative w-full bg-slate-100 border border-slate-200 rounded-lg overflow-auto min-h-[60vh] shadow-inner flex justify-center items-start p-6 md:p-12">
              <div ref={htmlPreviewRef} className="w-auto h-auto text-black relative" style={{ isolation: 'isolate' }}>
                <style dangerouslySetInnerHTML={{ __html: `
                  [data-cert-preview-root]{display:flex;flex-direction:column;align-items:center;gap:40px;padding:20px}
                  [data-cert-preview-root]>*{width:1200px;max-width:1200px;height:848px;box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);background:white}
                  [data-cert-preview-root] [style*="flex-direction: row"]{flex-direction:column !important}
                  [data-cert-preview-root] [style*="grid-auto-flow: column"]{grid-auto-flow:row !important}
                  [data-cert-preview-root] [style*="grid-template-columns"]{grid-template-columns:1fr !important}
                ` }} />
                <div data-cert-preview-root dangerouslySetInnerHTML={{
                  __html: buildPreviewHtml(model.customTextFront || model.html || model.meta?.html || '', overrides)
                }} />
              </div>
            </div>
          ) : (
            <div className="relative w-full bg-slate-950 border border-slate-800 rounded-lg overflow-auto flex justify-center p-6 md:p-12">
              <div ref={previewAreaRef} className="relative w-[1200px] h-[848px] shrink-0 bg-white shadow-2xl">
                {model.frontImage ? (
                  <img src={model.frontImage} className="absolute inset-0 w-full h-full object-contain" alt="Preview" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs font-mono">Sem Imagem (Preview)</div>
                )}

                {!model.useHtml && (
                  <div className="absolute inset-0 overflow-hidden" style={{ pointerEvents: 'none' }}>
                    <div
                      className="absolute prose prose-slate max-w-none text-slate-800"
                      style={{
                        left: `${layout.front.x}%`,
                        top: `${layout.front.y}%`,
                        width: `${layout.front.width}%`,
                        lineHeight: layout.front.lineHeight,
                        transform: 'translate(0, 0)',
                      }}
                      dangerouslySetInnerHTML={{ __html: buildPreviewHtml(model.customTextFront || '', overrides) }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Hidden container for PDF export as per project memory */}
        <div style={{ position: 'fixed', left: '-9999px', top: '-9999px', zIndex: -50, visibility: 'hidden' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            
            {/* Full Document Container */}
            <div
              ref={frontPrintRef}
              style={{ backgroundColor: '#ffffff', position: 'relative', color: '#0f172a' }}
            >
              {model.useHtml ? (
                <div
                  data-cert-root="true"
                  className="prose prose-slate max-w-none"
                  style={{ width: '1200px', position: 'relative', margin: 0, padding: 0, color: 'inherit' }}
                  dangerouslySetInnerHTML={{
                    __html: buildPreviewHtml(model.customTextFront || model.html || model.meta?.html || '', overrides)
                  }}
                />
              ) : (
                <div style={{ width: '1200px', height: '848px', position: 'relative' }}>
                  {model.frontImage && <img src={model.frontImage} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} alt="Preview" crossOrigin="anonymous" />}
                  <div style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}>
                    <div
                      className="absolute prose prose-slate max-w-none"
                      style={{
                        left: `${layout.front.x}%`,
                        top: `${layout.front.y}%`,
                        width: `${layout.front.width}%`,
                        lineHeight: layout.front.lineHeight,
                        transform: 'translate(0, 0)',
                        color: 'inherit'
                      }}
                      dangerouslySetInnerHTML={{ __html: buildPreviewHtml(model.customTextFront || '', overrides) }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Legacy Back Container (Only used for non-HTML) */}
            {!model.useHtml && (
              <div
                ref={backPrintRef}
                style={{ width: '1200px', height: '848px', backgroundColor: '#ffffff', position: 'relative', color: '#0f172a' }}
              >
                <div style={{ width: '1200px', height: '848px', position: 'relative' }}>
                  {model.backImage && <img src={model.backImage} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} alt="Verso" crossOrigin="anonymous" />}
                  <div style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}>
                    <div
                      className="absolute prose prose-slate max-w-none"
                      style={{
                        left: `${layout.back.x}%`,
                        top: `${layout.back.y}%`,
                        width: `${layout.back.width}%`,
                        lineHeight: layout.back.lineHeight,
                        transform: 'translate(0, 0)',
                        color: 'inherit'
                      }}
                      dangerouslySetInnerHTML={{ __html: buildPreviewHtml(buildBackStackHtml(model), overrides) }}
                    />
                    {normalizeBackFromLegacy(model).showQr && (
                      <div
                        className="absolute"
                        style={{ left: `${layout.back.qr.x}%`, top: `${layout.back.qr.y}%`, transform: 'translate(-50%, -50%)' }}
                      >
                        <img src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(overrides.qrcode_url || '')}`} style={{ width: '96px', height: '96px', border: '4px solid white', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} alt="QR Code" crossOrigin="anonymous" />
                      </div>
                    )}
                    <div
                      className="absolute"
                      style={{ left: `${layout.back.declaration.x}%`, top: `${layout.back.declaration.y}%`, width: `${layout.back.declaration.width}%`, transform: 'translate(-50%, -50%)', textAlign: 'center' }}
                    >
                      <div className="prose prose-slate max-w-none" style={{ color: 'inherit' }} dangerouslySetInnerHTML={{ __html: buildPreviewHtml(model.backDeclarationHtml || '', overrides) || '<p style="color:#64748b;margin:0;">Declaração Institucional</p>' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
